import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Pencil, Trophy, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { ImageUpload, FileUpload } from "@/pages/admin/AdminUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function Homepage() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/settings").then((r) => setS({ featured_recommendation: {}, recommended_tools: [], mentorship_benefits: [], ...r.data })); }, []);
  if (!s) return <Loader />;
  const fr = s.featured_recommendation || {};
  const setFr = (k, v) => set("featured_recommendation", { ...fr, [k]: v });
  const save = async () => {
    await api.put("/admin/settings", {
      hero_headline: s.hero_headline, hero_tagline: s.hero_tagline, hero_image: s.hero_image, coach_name: s.coach_name,
      support_notice: s.support_notice, featured_recommendation: fr, students_override: s.students_override,
      recommended_tools: s.recommended_tools, calendly_url: s.calendly_url, coach_email: s.coach_email,
      mentorship_details: s.mentorship_details, mentorship_benefits: s.mentorship_benefits, mentorship_payment_note: s.mentorship_payment_note,
      niche_courses_enabled: !!s.niche_courses_enabled,
    });
    toast.success("Homepage settings saved");
  };
  const tools = s.recommended_tools || [];
  const setTool = (i, k, v) => { const t = [...tools]; t[i] = { ...t[i], [k]: v }; set("recommended_tools", t); };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-heading font-semibold">Hero</h3>
        <div><Label>Headline</Label><Textarea value={s.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="hero-headline" /></div>
        <div><Label>Tagline</Label><Textarea value={s.hero_tagline || ""} onChange={(e) => set("hero_tagline", e.target.value)} data-testid="hero-tagline" /></div>
        <div><Label>Coach Name</Label><Input value={s.coach_name || ""} onChange={(e) => set("coach_name", e.target.value)} data-testid="coach-name" /></div>
        <ImageUpload label="Hero / Coach photo" value={s.hero_image} onChange={(v) => set("hero_image", v)} testId="hero-image-upload" />
        <div><Label>Support Notice</Label><Input value={s.support_notice || ""} onChange={(e) => set("support_notice", e.target.value)} data-testid="support-notice-input" /></div>
        <div><Label>"Students Learning" count (leave blank to use real count)</Label><Input type="number" value={s.students_override ?? ""} onChange={(e) => set("students_override", e.target.value)} placeholder="e.g. 1247" data-testid="students-override" /></div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-heading font-semibold">Featured Recommendation</h3>
        <div><Label>Title</Label><Input value={fr.title || ""} onChange={(e) => setFr("title", e.target.value)} data-testid="fr-title" /></div>
        <div><Label>Description</Label><Textarea value={fr.description || ""} onChange={(e) => setFr("description", e.target.value)} data-testid="fr-desc" /></div>
        <div><Label>Learn More URL</Label><Input value={fr.url || ""} onChange={(e) => setFr("url", e.target.value)} data-testid="fr-url" /></div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Recommended Tools</h3>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => set("recommended_tools", [...tools, { name: "", description: "", url: "" }])} data-testid="add-tool">Add</Button>
        </div>
        {tools.map((t, i) => (
          <div key={i} className="p-3 rounded-xl bg-secondary/40 space-y-2" data-testid={`tool-edit-${i}`}>
            <div className="flex gap-2"><Input placeholder="Name" value={t.name} onChange={(e) => setTool(i, "name", e.target.value)} /><Button size="sm" variant="ghost" onClick={() => set("recommended_tools", tools.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
            <Input placeholder="Description" value={t.description} onChange={(e) => setTool(i, "description", e.target.value)} />
            <Input placeholder="Learn More URL" value={t.url} onChange={(e) => setTool(i, "url", e.target.value)} />
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-heading font-semibold">Mentorship</h3>
        <p className="text-xs text-muted-foreground">Mentorship now uses the built-in scheduling calendar. Manage available time slots under <span className="font-medium">Products &amp; Access → Mentorship</span>.</p>
        <div><Label>Coach Notification Email</Label><Input value={s.coach_email || ""} onChange={(e) => set("coach_email", e.target.value)} data-testid="coach-email" /></div>
        <div><Label>Details</Label><Textarea value={s.mentorship_details || ""} onChange={(e) => set("mentorship_details", e.target.value)} data-testid="mentorship-details" /></div>
        <div><Label>Benefits (one per line)</Label><Textarea value={(s.mentorship_benefits || []).join("\n")} onChange={(e) => set("mentorship_benefits", e.target.value.split("\n").filter(Boolean))} data-testid="mentorship-benefits" /></div>
        <div><Label>Payment Note</Label><Input value={s.mentorship_payment_note || ""} onChange={(e) => set("mentorship_payment_note", e.target.value)} data-testid="mentorship-payment-note" /></div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><span className="font-medium">Additional Niche Courses live</span><br /><span className="text-muted-foreground text-xs">Off = students see a "Coming Soon" preview. On = the niche courses store is fully accessible.</span></span>
          <Switch checked={!!s.niche_courses_enabled} onCheckedChange={(v) => set("niche_courses_enabled", v)} data-testid="toggle-niche" />
        </label>
      </div>

      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-homepage"><Save className="w-4 h-4 mr-1.5" /> Save All</Button>
    </div>
  );
}

const emptyWin = { student_name: "", niche: "", rate: "", achievement: "", story: "", photo_url: null, date: "", featured: false, published: true };
function Wins() {
  const [wins, setWins] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyWin);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = async () => setWins((await api.get("/admin/wins")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.student_name || !form.niche) return toast.error("Name and niche required");
    const payload = { ...form, date: form.date || new Date().toISOString() };
    if (form.id) await api.put(`/admin/wins/${form.id}`, payload); else await api.post("/admin/wins", payload);
    toast.success("Saved"); setOpen(false); setForm(emptyWin); load();
  };
  const del = async (id) => { await api.delete(`/admin/wins/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm(emptyWin); setOpen(true); }} data-testid="add-win"><Plus className="w-4 h-4 mr-1.5" /> Add Win</Button></div>
      {!wins ? <Loader /> : wins.length === 0 ? <EmptyState icon={Trophy} title="No student wins yet" description="Add real student wins only — never fabricated." testId="wins-admin-empty" /> : (
        <div className="space-y-3">
          {wins.map((w) => (
            <div key={w.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`win-${w.id}`}>
              <div><p className="font-medium">{w.student_name} {w.featured && <span className="text-warning text-xs">★</span>}</p><p className="text-xs text-muted-foreground">{w.niche} {w.rate && `· ${w.rate}`}</p></div>
              <div className="flex items-center gap-2">
                <StatusBadge status={w.published ? "approved" : "rejected"} />
                <Button size="sm" variant="ghost" onClick={() => { setForm(w); setOpen(true); }} data-testid={`edit-win-${w.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(w.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Student Win</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Student Name *</Label><Input value={form.student_name} onChange={(e) => set("student_name", e.target.value)} data-testid="win-name" /></div>
              <div><Label>Niche *</Label><Input value={form.niche} onChange={(e) => set("niche", e.target.value)} data-testid="win-niche" /></div>
              <div><Label>Hourly Rate</Label><Input value={form.rate} onChange={(e) => set("rate", e.target.value)} placeholder="$30/hr" data-testid="win-rate" /></div>
              <div><Label>Date</Label><Input type="date" value={form.date?.slice(0, 10) || ""} onChange={(e) => set("date", e.target.value)} data-testid="win-date" /></div>
            </div>
            <div><Label>Achievement / Title</Label><Input value={form.achievement} onChange={(e) => set("achievement", e.target.value)} data-testid="win-achievement" /></div>
            <div><Label>Story</Label><Textarea value={form.story} onChange={(e) => set("story", e.target.value)} data-testid="win-story" /></div>
            <ImageUpload label="Photo (optional)" value={form.photo_url} onChange={(v) => set("photo_url", v)} testId="win-photo" />
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="win-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="win-published" /> Published</label>
            </div>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-win">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Faqs() {
  const [faqs, setFaqs] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "" });
  const load = async () => setFaqs((await api.get("/faqs")).data);
  useEffect(() => { load(); }, []);
  const save = async () => { if (!form.question) return; if (form.id) await api.put(`/admin/faqs/${form.id}`, form); else await api.post("/admin/faqs", form); setForm({ question: "", answer: "" }); load(); toast.success("Saved"); };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };
  const move = async (i, d) => { const ids = faqs.map((x) => x.id); const j = i + d; if (j < 0 || j >= ids.length) return; [ids[i], ids[j]] = [ids[j], ids[i]]; await api.post("/admin/faqs/reorder", { ids }); load(); };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-3">
        <h3 className="font-heading font-semibold">{form.id ? "Edit FAQ" : "Add FAQ"}</h3>
        <Input placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" />
        <Textarea placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer" />
        <div className="flex gap-2">
          <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-faq">{form.id ? "Update" : "Add"}</Button>
          {form.id && <Button variant="outline" className="rounded-full" onClick={() => setForm({ question: "", answer: "" })}>Cancel</Button>}
        </div>
      </div>
      {!faqs ? <Loader /> : faqs.map((f, i) => (
        <div key={f.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-start justify-between gap-3" data-testid={`faq-${f.id}`}>
          <div><p className="font-medium">{f.question}</p><p className="text-sm text-muted-foreground mt-1">{f.answer}</p></div>
          <div className="flex gap-0.5 shrink-0">
            <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(f)}><Pencil className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => del(f.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoWalkthrough() {
  const [v, setV] = useState(null);
  const set = (k, val) => setV((x) => ({ ...x, [k]: val }));
  useEffect(() => { api.get("/admin/video-section").then((r) => setV(r.data)); }, []);
  if (!v) return <Loader />;
  const save = async () => {
    await api.put("/admin/video-section", v);
    toast.success("Video walkthrough saved");
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-video-section">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><span className="font-medium">Section visible to students</span><br /><span className="text-muted-foreground text-xs">Off = the Video Walkthrough page and welcome card are hidden.</span></span>
          <Switch checked={v.enabled !== false} onCheckedChange={(val) => set("enabled", val)} data-testid="video-enabled" />
        </label>
        <div><Label>Title</Label><Input value={v.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="video-adm-title" /></div>
        <div><Label>Description</Label><Textarea value={v.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="video-adm-desc" /></div>
        <div><Label>Unlock instructions (shown before payment)</Label><Textarea value={v.instructions || ""} onChange={(e) => set("instructions", e.target.value)} data-testid="video-adm-instructions" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Price</Label><Input value={v.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="₱499" data-testid="video-adm-price" /></div>
        </div>
        <ImageUpload label="Preview image (shown before purchase)" value={v.image_url} onChange={(val) => set("image_url", val)} testId="video-adm-image" />
        <div><Label>Video access link (Google Drive / any URL — emailed on confirmation)</Label><Input value={v.video_link || ""} onChange={(e) => set("video_link", e.target.value)} placeholder="https://drive.google.com/file/d/.../view" data-testid="video-adm-link" /></div>
        <p className="text-xs text-muted-foreground">Students never see this link until you approve their payment under Products &amp; Access → Payment Proofs. On approval the link is auto-emailed and unlocked.</p>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-video-section"><Save className="w-4 h-4 mr-1.5" /> Save Video Section</Button>
      </div>
    </div>
  );
}

function WelcomeToggle() {
  const [wp, setWp] = useState(null);
  const set = (k, val) => setWp((x) => ({ ...x, [k]: val }));
  useEffect(() => { api.get("/home").then((r) => setWp(r.data.welcome_popup || { enabled: true, title: "", message: "" })); }, []);
  if (!wp) return <Loader />;
  const save = async () => { await api.put("/admin/welcome-popup", wp); toast.success("Welcome pop-up saved"); };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-welcome-popup">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><span className="font-medium">Show welcome pop-up to students</span><br /><span className="text-muted-foreground text-xs">Appears once per session after login with quick links to Course, Video &amp; Mentorship.</span></span>
          <Switch checked={wp.enabled !== false} onCheckedChange={(val) => set("enabled", val)} data-testid="welcome-enabled" />
        </label>
        <div><Label>Title</Label><Input value={wp.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="welcome-title" /></div>
        <div><Label>Message</Label><Textarea value={wp.message || ""} onChange={(e) => set("message", e.target.value)} data-testid="welcome-message" /></div>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-welcome-popup"><Save className="w-4 h-4 mr-1.5" /> Save Pop-up</Button>
      </div>
    </div>
  );
}

function Announcements() {
  const [form, setForm] = useState({ title: "", body: "", link: "", audience: "all", emails: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const payload = { title: form.title, body: form.body, link: form.link };
      if (form.audience === "specific") {
        payload.emails = form.emails.split(",").map((s) => s.trim()).filter(Boolean);
        if (!payload.emails.length) { setBusy(false); return toast.error("Enter at least one student email"); }
      }
      const { data } = await api.post("/admin/announcements", payload);
      toast.success(`Announcement sent to ${data.sent} student${data.sent === 1 ? "" : "s"}`);
      setForm({ title: "", body: "", link: "", audience: "all", emails: "" });
    } catch { toast.error("Failed to send announcement"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-announcements">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <p className="text-sm text-muted-foreground">Send an in-app notification to students. They'll see it in their notification bell.</p>
        <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. New lesson added to Phase 3" data-testid="ann-title" /></div>
        <div><Label>Message</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-body" /></div>
        <div><Label>Link (optional, e.g. /course)</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/course" data-testid="ann-link" /></div>
        <div>
          <Label>Send to</Label>
          <div className="flex gap-2 mt-1">
            <Button type="button" variant={form.audience === "all" ? "default" : "outline"} className={`rounded-full ${form.audience === "all" ? "bg-teal hover:bg-teal/90" : ""}`} onClick={() => setForm({ ...form, audience: "all" })} data-testid="ann-audience-all">All students</Button>
            <Button type="button" variant={form.audience === "specific" ? "default" : "outline"} className={`rounded-full ${form.audience === "specific" ? "bg-teal hover:bg-teal/90" : ""}`} onClick={() => setForm({ ...form, audience: "specific" })} data-testid="ann-audience-specific">Specific students</Button>
          </div>
        </div>
        {form.audience === "specific" && (
          <div><Label>Student emails (comma separated)</Label><Textarea value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })} placeholder="jane@example.com, mark@example.com" data-testid="ann-emails" /></div>
        )}
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={send} disabled={busy} data-testid="send-announcement"><Save className="w-4 h-4 mr-1.5" /> Send Announcement</Button>
      </div>
    </div>
  );
}

function PlaybookCardEditor() {
  const [c, setC] = useState(null);
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/home").then((r) => setC(r.data.playbook_card || {})); }, []);
  if (!c) return <Loader />;
  const save = async () => { await api.put("/admin/playbook-card", c); toast.success("Playbook card saved"); };
  const img = c.image_url ? (c.image_url.startsWith("http") ? c.image_url : process.env.REACT_APP_BACKEND_URL + c.image_url) : null;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-playbook">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium">Show the premium Playbook card on the homepage</span>
          <Switch checked={c.enabled !== false} onCheckedChange={(v) => set("enabled", v)} data-testid="pb-enabled" />
        </label>
        <ImageUpload label="Background photo (any size)" value={c.image_url} onChange={(v) => set("image_url", v)} testId="pb-image" />
        <FileUpload label="Preview video (horizontal — muted autoplay on scroll; keep it short, under ~30MB)" value={c.video_url} filename={c.video_name} onChange={(url, name) => setC((x) => ({ ...x, video_url: url, video_name: name }))} accept="video/*" testId="pb-video" />
        <p className="text-xs text-muted-foreground -mt-2">If a video is uploaded it replaces the background image. Use the zoom &amp; position sliders below to frame Coach Reyland waist-up.</p>
        <div><Label>Zoom ({(c.zoom || 1).toFixed(2)}x)</Label><input type="range" min="1" max="2.5" step="0.05" value={c.zoom || 1} onChange={(e) => set("zoom", parseFloat(e.target.value))} className="w-full accent-teal" data-testid="pb-zoom" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Horizontal ({c.pos_x ?? 50}%)</Label><input type="range" min="0" max="100" value={c.pos_x ?? 50} onChange={(e) => set("pos_x", parseInt(e.target.value))} className="w-full accent-teal" data-testid="pb-x" /></div>
          <div><Label>Vertical ({c.pos_y ?? 50}%)</Label><input type="range" min="0" max="100" value={c.pos_y ?? 50} onChange={(e) => set("pos_y", parseInt(e.target.value))} className="w-full accent-teal" data-testid="pb-y" /></div>
        </div>
        <div><Label>Signature script text</Label><Input value={c.script_text || ""} onChange={(e) => set("script_text", e.target.value)} data-testid="pb-script" /></div>
        <div><Label>Tag</Label><Input value={c.tag || ""} onChange={(e) => set("tag", e.target.value)} data-testid="pb-tag" /></div>
        <div className="relative rounded-2xl overflow-hidden h-56" data-testid="pb-preview">
          {img ? <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${c.pos_x ?? 50}% ${c.pos_y ?? 50}%`, transform: `scale(${c.zoom || 1})` }} /> : <div className="absolute inset-0 bg-navy" />}
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <span className="text-[10px] font-semibold text-white bg-teal/80 px-2 py-1 rounded-full">{c.tag}</span>
            <p className="font-script text-3xl text-teal-soft mt-1">{c.script_text || "Coach Reyland"}</p>
          </div>
        </div>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-playbook"><Save className="w-4 h-4 mr-1.5" /> Save Playbook Card</Button>
      </div>
    </div>
  );
}

function HeroCardEditor() {
  const [c, setC] = useState(null);
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/home").then((r) => setC(r.data.hero_card || {})); }, []);
  if (!c) return <Loader />;
  const save = async () => { await api.put("/admin/hero-card", c); toast.success("Hero card saved"); };
  const img = c.image_url ? (c.image_url.startsWith("http") ? c.image_url : process.env.REACT_APP_BACKEND_URL + c.image_url) : null;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-hero-card">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <ImageUpload label="Coach photo (transparent PNG recommended)" value={c.image_url} onChange={(v) => set("image_url", v)} testId="hc-image" />
        <div><Label>Zoom ({(c.zoom || 1).toFixed(2)}x)</Label><input type="range" min="1" max="2.5" step="0.05" value={c.zoom || 1} onChange={(e) => set("zoom", parseFloat(e.target.value))} className="w-full accent-teal" data-testid="hc-zoom" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Horizontal ({c.pos_x ?? 50}%)</Label><input type="range" min="0" max="100" value={c.pos_x ?? 50} onChange={(e) => set("pos_x", parseInt(e.target.value))} className="w-full accent-teal" data-testid="hc-x" /></div>
          <div><Label>Vertical ({c.pos_y ?? 50}%)</Label><input type="range" min="0" max="100" value={c.pos_y ?? 50} onChange={(e) => set("pos_y", parseInt(e.target.value))} className="w-full accent-teal" data-testid="hc-y" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Badge title</Label><Input value={c.badge_title || ""} onChange={(e) => set("badge_title", e.target.value)} data-testid="hc-badge-title" /></div>
          <div><Label>Badge subtitle</Label><Input value={c.badge_subtitle || ""} onChange={(e) => set("badge_subtitle", e.target.value)} data-testid="hc-badge-subtitle" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={c.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="hc-name" /></div>
          <div><Label>Subtitle</Label><Input value={c.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="hc-subtitle" /></div>
        </div>
        <div className="max-w-[300px] mx-auto">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-gradient-to-b from-teal/10 to-brandgreen/10" data-testid="hc-preview">
            {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${c.pos_x ?? 50}% ${c.pos_y ?? 50}%`, transform: `scale(${c.zoom || 1})` }} />}
            <div className="absolute top-3 right-3 flex items-center gap-2 bg-white/95 rounded-full pl-2 pr-3 py-1.5 shadow">
              <span className="w-6 h-6 rounded-full bg-teal/10 flex items-center justify-center"><Trophy className="w-3 h-3 text-teal" /></span>
              <div className="leading-tight"><p className="text-[11px] font-bold text-navy">{c.badge_title || "Upwork Mentor"}</p><p className="text-[9px] text-muted-foreground">{c.badge_subtitle || "Trusted Mentor"}</p></div>
            </div>
          </div>
          <div className="text-center mt-3"><p className="font-heading font-bold text-navy">{c.name || "Coach Reyland"}</p><p className="text-xs text-teal font-semibold">{c.subtitle || "Upwork Success Mentor"}</p></div>
        </div>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-hero-card"><Save className="w-4 h-4 mr-1.5" /> Save Hero Card</Button>
      </div>
    </div>
  );
}

function MentorshipWall() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ image_url: "", name: "", label: "" });
  const load = () => api.get("/admin/mentorship-testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader />;
  const add = async () => {
    if (!form.image_url) return toast.error("Upload a screenshot first");
    await api.post("/admin/mentorship-testimonials", form);
    toast.success("Added to the wall"); setForm({ image_url: "", name: "", label: "" }); load();
  };
  const del = async (id) => { await api.delete(`/admin/mentorship-testimonials/${id}`); load(); };
  const img = (u) => u?.startsWith("http") ? u : process.env.REACT_APP_BACKEND_URL + u;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-wall">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-4">
        <p className="text-sm text-muted-foreground">Upload student feedback screenshots. They appear in the homepage "What They Say About My Mentorship" moving collage. Clicking a card opens the full screenshot with the name and label.</p>
        <ImageUpload label="Screenshot" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="wall-image" />
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Name / initials</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. J.M." data-testid="wall-name" /></div>
          <div><Label>Short label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Upwork earnings after 3 weeks of mentorship" data-testid="wall-label" /></div>
        </div>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={add} data-testid="wall-add"><Plus className="w-4 h-4 mr-1.5" /> Add to Wall</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((t) => (
          <div key={t.id} className="relative rounded-xl overflow-hidden border border-border bg-white" data-testid={`wall-item-${t.id}`}>
            <img src={img(t.image_url)} alt="" className="w-full h-40 object-cover" />
            <div className="p-2"><p className="text-xs font-semibold">{t.name || "—"}</p><p className="text-[11px] text-muted-foreground line-clamp-2">{t.label}</p></div>
            <button onClick={() => del(t.id)} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`wall-del-${t.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomepageDataEditor() {
  const [graph, setGraph] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/home").then((r) => { setGraph(JSON.stringify(r.data.growth_graph, null, 2)); setRows(r.data.country_leaderboard || []); }); }, []);
  const saveGraph = async () => { try { await api.put("/admin/growth-graph", JSON.parse(graph)); toast.success("Graph saved"); } catch { toast.error("Invalid JSON — check the format"); } };
  const saveRows = async () => { await api.put("/admin/country-leaderboard", { rows }); toast.success("Leaderboard saved"); };
  const setRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: k === "count" ? (parseInt(v) || 0) : v } : r));
  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-homedata">
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Demand graph data</p>
        <p className="text-xs text-muted-foreground">Edit the chart lines and monthly data points (JSON). Each data row needs the keys from "lines".</p>
        <textarea className="w-full h-64 font-mono text-xs border border-border rounded-xl p-3" value={graph} onChange={(e) => setGraph(e.target.value)} data-testid="graph-json" />
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={saveGraph} data-testid="save-graph">Save Graph</Button>
      </div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Top Students by Country</p>
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={r.country} onChange={(e) => setRow(i, "country", e.target.value)} placeholder="Country" data-testid={`lb-country-${i}`} />
            <Input type="number" className="w-24" value={r.count} onChange={(e) => setRow(i, "count", e.target.value)} data-testid={`lb-count-${i}`} />
            <button className="text-red-500" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} data-testid={`lb-del-${i}`}>✕</button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setRows([...rows, { country: "", count: 0 }])} data-testid="lb-add">Add country</Button>
          <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={saveRows} data-testid="save-leaderboard">Save Leaderboard</Button>
        </div>
      </div>
    </div>
  );
}

function AIConfigEditor() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/ai-config").then((r) => setC(r.data)); }, []);
  if (!c) return <Loader />;
  const setTier = (i, k, v) => setC({ ...c, pricing: c.pricing.map((p, idx) => idx === i ? { ...p, [k]: v } : p) });
  const save = async () => { await api.put("/admin/ai-config", c); toast.success("AI settings saved"); };
  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-aiconfig">
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Pro tip banner</p>
        <textarea className="w-full h-20 border border-border rounded-xl p-3 text-sm" value={c.pro_tip} onChange={(e) => setC({ ...c, pro_tip: e.target.value })} data-testid="ai-protip-edit" />
        <p className="font-heading font-bold pt-2">AI proposal prompt</p>
        <textarea className="w-full h-56 font-mono text-xs border border-border rounded-xl p-3" value={c.prompt} onChange={(e) => setC({ ...c, prompt: e.target.value })} data-testid="ai-prompt-edit" />
      </div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Payment QR code</p>
        <p className="text-xs text-muted-foreground">Shown to students when they request a Standard or Top Rated upgrade.</p>
        <ImageUpload value={c.qr_url} onChange={(url) => setC({ ...c, qr_url: url || "" })} label="Upgrade payment QR" testId="ai-qr-upload" />
      </div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-4">
        <p className="font-heading font-bold">Pricing tiers</p>
        {(c.pricing || []).map((p, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 border border-border rounded-xl p-3">
            <Input value={p.name} onChange={(e) => setTier(i, "name", e.target.value)} placeholder="Name" data-testid={`tier-name-${i}`} />
            <Input value={p.price} onChange={(e) => setTier(i, "price", e.target.value)} placeholder="Price" data-testid={`tier-price-${i}`} />
            <Input value={p.per} onChange={(e) => setTier(i, "per", e.target.value)} placeholder="Allowance" data-testid={`tier-per-${i}`} />
            <Input value={p.stars ?? ""} type="number" step="0.5" onChange={(e) => setTier(i, "stars", parseFloat(e.target.value) || 0)} placeholder="Stars (0-5)" data-testid={`tier-stars-${i}`} />
            <Input value={p.cta || ""} onChange={(e) => setTier(i, "cta", e.target.value)} placeholder="Button label" data-testid={`tier-cta-${i}`} />
            <Input value={p.disclaimer || ""} onChange={(e) => setTier(i, "disclaimer", e.target.value)} placeholder="Red alert (optional)" data-testid={`tier-disclaimer-${i}`} />
            <Textarea className="col-span-2" rows={3} value={(p.features || []).join("\n")} onChange={(e) => setTier(i, "features", e.target.value.split("\n").filter(Boolean))} placeholder="Features, one per line" data-testid={`tier-features-${i}`} />
          </div>
        ))}
      </div>
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-ai-config">Save AI Settings</Button>
    </div>
  );
}

function MarketInsightsEditor() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/market-insights").then((r) => setM(r.data)); }, []);
  if (!m) return <Loader />;
  const save = async () => { await api.put("/admin/market-insights", m); toast.success("Market insights saved"); };
  const setStat = (i, k, v) => setM({ ...m, stats: m.stats.map((s, idx) => idx === i ? { ...s, [k]: v } : s) });
  const setCountry = (i, k, v) => setM({ ...m, countries: m.countries.map((s, idx) => idx === i ? { ...s, [k]: v } : s) });
  const setChart = (k, v) => setM({ ...m, chart: { ...m.chart, [k]: v } });
  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-market">
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Chart (Job Postings & Hires Trend)</p>
        <div className="grid grid-cols-2 gap-2">
          <Input value={m.chart?.range_label || ""} onChange={(e) => setChart("range_label", e.target.value)} placeholder="Range label" data-testid="mkt-range" />
          <Input value={m.chart?.trend_tooltip || ""} onChange={(e) => setChart("trend_tooltip", e.target.value)} placeholder="Last-bar tooltip" data-testid="mkt-tooltip" />
        </div>
        <p className="text-xs text-muted-foreground">Chart data points (JSON array of {"{ name, posts, hires }"})</p>
        <textarea className="w-full h-40 font-mono text-xs border border-border rounded-xl p-3" value={JSON.stringify(m.chart?.data || [], null, 2)}
          onChange={(e) => { try { setChart("data", JSON.parse(e.target.value)); } catch { /* keep typing */ } }} data-testid="mkt-data" />
      </div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Stat cards</p>
        {(m.stats || []).map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 border border-border rounded-xl p-3">
            <Input value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="Value" data-testid={`mkt-stat-value-${i}`} />
            <Input value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Label" data-testid={`mkt-stat-label-${i}`} />
            <Input value={s.sub || ""} onChange={(e) => setStat(i, "sub", e.target.value)} placeholder="Sub note (optional)" data-testid={`mkt-stat-sub-${i}`} />
            <Input value={s.badge || ""} onChange={(e) => setStat(i, "badge", e.target.value)} placeholder="Badge (optional)" data-testid={`mkt-stat-badge-${i}`} />
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
        <p className="font-heading font-bold">Top countries</p>
        {(m.countries || []).map((s, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 border border-border rounded-xl p-3">
            <Input value={s.flag} onChange={(e) => setCountry(i, "flag", e.target.value)} placeholder="Flag (emoji)" data-testid={`mkt-country-flag-${i}`} />
            <Input value={s.name} onChange={(e) => setCountry(i, "name", e.target.value)} placeholder="Country" data-testid={`mkt-country-name-${i}`} />
            <Input value={s.hires} onChange={(e) => setCountry(i, "hires", e.target.value)} placeholder="Hire count" data-testid={`mkt-country-hires-${i}`} />
          </div>
        ))}
      </div>
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-market">Save Market Insights</Button>
    </div>
  );
}

export default function AdminContent() {
  return (
    <div>
      <PageHeader title="Homepage & Content" subtitle="Manage all live homepage content" />
      <Tabs defaultValue="home">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="home" data-testid="tab-home">Homepage</TabsTrigger>
          <TabsTrigger value="herocard" data-testid="tab-herocard">Hero Card</TabsTrigger>
          <TabsTrigger value="wins" data-testid="tab-wins">Student Wins</TabsTrigger>
          <TabsTrigger value="wall" data-testid="tab-wall">Mentorship Wall</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video">Video Walkthrough</TabsTrigger>
          <TabsTrigger value="welcome" data-testid="tab-welcome">Welcome Pop-up</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
          <TabsTrigger value="playbook" data-testid="tab-playbook">Playbook Card</TabsTrigger>
          <TabsTrigger value="homedata" data-testid="tab-homedata">Graph & Leaderboard</TabsTrigger>
          <TabsTrigger value="aiconfig" data-testid="tab-aiconfig">AI Generator</TabsTrigger>
          <TabsTrigger value="market" data-testid="tab-market">Market Insights</TabsTrigger>
        </TabsList>
        <TabsContent value="home"><Homepage /></TabsContent>
        <TabsContent value="herocard"><HeroCardEditor /></TabsContent>
        <TabsContent value="wins"><Wins /></TabsContent>
        <TabsContent value="wall"><MentorshipWall /></TabsContent>
        <TabsContent value="faqs"><Faqs /></TabsContent>
        <TabsContent value="video"><VideoWalkthrough /></TabsContent>
        <TabsContent value="welcome"><WelcomeToggle /></TabsContent>
        <TabsContent value="announcements"><Announcements /></TabsContent>
        <TabsContent value="playbook"><PlaybookCardEditor /></TabsContent>
        <TabsContent value="homedata"><HomepageDataEditor /></TabsContent>
        <TabsContent value="aiconfig"><AIConfigEditor /></TabsContent>
        <TabsContent value="market"><MarketInsightsEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
